import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Heading,
  Alert,
  AlertIcon,
  useToast,
  Text,
  Flex,
  Link,
  Stack,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Select,
  Container,
  Grid,
  Icon,
  VStack,
} from '@chakra-ui/react';
import {
  FaWhatsapp,
  FaInstagram,
  FaFacebook,
  FaYoutube,
  FaTiktok,
  FaMapMarkerAlt,
  FaEnvelope,
  FaPhoneAlt,
} from 'react-icons/fa';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { standardizeError } from '../utils';
import { track } from '../utils/umami';
import { C, Eyebrow, PrimaryButton } from './ui/SiteKit';

// Valori ammessi per reason (ordine e lista ufficiale); in UI si mostrano sempre con etichette senza trattini
const REASON_OPTIONS = [
  'informazioni-generali',
  'info-eventi',
  'info-centro-estivo',
  'magic',
  'giochi-societa',
  'ludodidattica',
  'iscrizione-socio',
  'rinnovo-socio',
  'proposta-progetto',
  'volontariato',
  'donazioni',
  'amministrazione-segreteria',
  'supporto-tecnico',
  'feedback',
];

const REASON_LABELS = {
  'informazioni-generali': 'Informazioni Generali',
  'info-eventi': 'Info su Eventi',
  'info-centro-estivo': 'Info Centro Estivo',
  magic: 'Magic: The Gathering',
  'giochi-societa': 'Giochi di Società',
  ludodidattica: 'Ludodidattica',
  'iscrizione-socio': 'Iscrizione Socio',
  'rinnovo-socio': 'Rinnovo Iscrizione',
  'proposta-progetto': 'Proposta Progetto / Partnership',
  volontariato: 'Volontariato',
  donazioni: 'Donazioni / Finanziamenti',
  'amministrazione-segreteria': 'Amministrazione / Segreteria',
  'supporto-tecnico': 'Supporto Tecnico',
  feedback: 'Feedback / Suggerimenti',
};

const formatReasonLabel = (value) =>
  REASON_LABELS[value] ||
  String(value)
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');

const ContactForm = ({
  title = 'Contattaci',
  description = 'Hai domande o idee? Siamo qui per ascoltarti!',
  titleSize = 'xl',
  variant = 'dark',
  maxW = 'lg',
  splitLayout = false,
}) => {
  const isLight = variant === 'light';
  const [formData, setFormData] = useState({
    email: '',
    content: '',
    reason: '',
    proposalSlug: '',
  });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reasonOptions, setReasonOptions] = useState([]);
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const propostaSlugFromUrl = searchParams.get('proposta')?.trim() || '';

  useEffect(() => {
    if (!propostaSlugFromUrl) return undefined;
    setFormData((prev) => {
      if (prev.proposalSlug === propostaSlugFromUrl) return prev;
      return {
        ...prev,
        reason: 'info-eventi',
        proposalSlug: propostaSlugFromUrl,
      };
    });
    return undefined;
  }, [propostaSlugFromUrl]);

  useEffect(() => {
    let cancelled = false;
    axios
      .get('/api/messages/reason-options')
      .then((res) => {
        const { data } = res;
        const raw = Array.isArray(data) ? data : (data?.data ?? []);
        const list =
          raw.length > 0
            ? REASON_OPTIONS.filter((v) => raw.includes(v))
            : REASON_OPTIONS;
        if (!cancelled) setReasonOptions(list);
      })
      .catch(() => {
        if (!cancelled) setReasonOptions(REASON_OPTIONS);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.reason) {
      setError('Seleziona il motivo del contatto.');
      return;
    }
    setIsSubmitting(true);

    try {
      const response = await axios.post('/api/messages', formData);
      if (response.status === 200) {
        if (formData.proposalSlug) {
          track.proposalRequest({
            proposta: formData.proposalSlug,
            canale: 'contatti',
          });
        } else {
          track.contactSubmit({ motivo: formData.reason });
        }
        toast({
          title: '🎉 Messaggio inviato!',
          description: 'Ti risponderemo il prima possibile!',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        setFormData({ email: '', content: '', reason: '', proposalSlug: '' });
      } else {
        setError('Ops! Qualcosa è andato storto.');
      }
    } catch (err) {
      const standardizedError = standardizeError(err);
      setError(standardizedError.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const labelColor = isLight ? 'gray.700' : 'gray.200';
  const menuButtonBg = isLight ? 'white' : 'gray.700';
  const menuListBg = isLight ? 'white' : 'gray.700';

  const formBlock = (
    <>
      {title && (
        <Heading
          as="h1"
          size={titleSize}
          textAlign={splitLayout ? { base: 'center', md: 'left' } : 'center'}
          mb={2}
          color={isLight ? 'gray.800' : 'white'}
          textShadow={isLight ? 'none' : '0 1px 2px rgba(0,0,0,0.3)'}
        >
          {title}
        </Heading>
      )}
      {description && (
        <Text
          textAlign={splitLayout ? { base: 'center', md: 'left' } : 'center'}
          fontSize="lg"
          mb={6}
          color={isLight ? 'gray.600' : 'gray.300'}
        >
          {description}
        </Text>
      )}

      {error && (
        <Alert status="error" mb={4} borderRadius="md">
          <AlertIcon />
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <FormControl id="email" mb={4} isRequired>
          <FormLabel color={labelColor}>Email</FormLabel>
          <Input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="La tua email"
            focusBorderColor="blue.400"
            _hover={{ borderColor: 'blue.400' }}
            bg={isLight ? 'white' : 'gray.700'}
            color={isLight ? 'gray.800' : 'white'}
            borderColor={isLight ? 'gray.300' : 'gray.600'}
            _placeholder={{ color: isLight ? 'gray.500' : 'gray.400' }}
          />
        </FormControl>

        <FormControl id="reason" mb={4} isRequired>
          <FormLabel color={labelColor}>Motivo</FormLabel>
          <Menu>
            <MenuButton
              as={Button}
              w="full"
              textAlign="left"
              isDisabled={Boolean(formData.proposalSlug)}
              bg={menuButtonBg}
              color={
                formData.reason
                  ? isLight
                    ? 'gray.800'
                    : 'white'
                  : isLight
                    ? 'gray.500'
                    : 'gray.400'
              }
              borderColor={isLight ? 'gray.300' : 'gray.600'}
              border="1px solid"
              _hover={{ borderColor: 'red.400', bg: menuButtonBg }}
              _focus={{ borderColor: 'red.400', bg: menuButtonBg }}
              _expanded={{ borderColor: 'red.400', bg: menuButtonBg }}
            >
              {formData.reason
                ? formatReasonLabel(formData.reason)
                : 'Seleziona il motivo di contatto'}
            </MenuButton>
            <MenuList
              bg={menuListBg}
              borderColor={isLight ? 'gray.200' : 'gray.600'}
            >
              {(reasonOptions.length ? reasonOptions : REASON_OPTIONS).map(
                (value) => (
                  <MenuItem
                    key={value}
                    value={value}
                    onClick={() => setFormData({ ...formData, reason: value })}
                    bg={menuListBg}
                    color={isLight ? 'gray.800' : 'white'}
                    _hover={{ bg: isLight ? 'gray.50' : 'gray.600' }}
                  >
                    {formatReasonLabel(value)}
                  </MenuItem>
                )
              )}
            </MenuList>
          </Menu>
        </FormControl>

        {formData.proposalSlug ? (
          <FormControl id="proposalSlug" mb={4}>
            <FormLabel color={labelColor}>Proposta selezionata</FormLabel>
            <Input
              value={formData.proposalSlug}
              isReadOnly
              bg={isLight ? 'gray.50' : 'gray.800'}
              color={isLight ? 'gray.800' : 'white'}
              borderColor={isLight ? 'gray.300' : 'gray.600'}
            />
          </FormControl>
        ) : null}

        <FormControl id="content" mb={6} isRequired>
          <FormLabel color={labelColor}>Messaggio</FormLabel>
          <Textarea
            name="content"
            value={formData.content}
            onChange={handleChange}
            placeholder="Scrivi il tuo messaggio qui..."
            rows={5}
            resize="vertical"
            focusBorderColor="pink.400"
            _hover={{ borderColor: 'pink.400' }}
            bg={isLight ? 'white' : 'gray.700'}
            color={isLight ? 'gray.800' : 'white'}
            borderColor={isLight ? 'gray.300' : 'gray.600'}
            _placeholder={{ color: isLight ? 'gray.500' : 'gray.400' }}
          />
        </FormControl>

        <Button
          type="submit"
          isLoading={isSubmitting}
          loadingText="Invio in corso..."
          colorScheme="blue"
          bg="blue.600"
          color="white"
          width="full"
          _hover={{ bg: 'blue.700', color: 'white' }}
          transition="all 0.3s"
          mb={splitLayout ? 0 : 8}
        >
          Invia Messaggio 🚀
        </Button>
      </form>
    </>
  );

  const contactsAndSocialBlock = (
    <Flex
      direction="row"
      align="center"
      justify="center"
      gap={8}
      color={isLight ? 'gray.700' : 'gray.200'}
      flexWrap="wrap"
    >
      <Stack spacing={2} align="start" direction="row" flexWrap="wrap" gap={6}>
        <Flex align="center" gap={2}>
          <FaWhatsapp size={20} />
          <Text fontSize="md">+39 353 468 2493</Text>
        </Flex>
        <Flex align="center" gap={2}>
          <FaEnvelope size={18} />
          <Text fontSize="md">info@cilassociazione.it</Text>
        </Flex>
        <Flex align="center" gap={2}>
          <FaMapMarkerAlt size={20} />
          <Text fontSize="md">Via Carlo Alberto 7, Chieri (TO)</Text>
        </Flex>
      </Stack>
      <Stack direction="row" gap={4} align="center">
        <Link href="https://www.instagram.com/cil_associazione" isExternal>
          <Box transition="transform 0.2s" _hover={{ transform: 'scale(1.2)' }}>
            <FaInstagram size={24} />
          </Box>
        </Link>
        <Link
          href="https://www.facebook.com/profile.php?id=100091128373973"
          isExternal
        >
          <Box transition="transform 0.2s" _hover={{ transform: 'scale(1.2)' }}>
            <FaFacebook size={24} />
          </Box>
        </Link>
        <Link href="https://www.youtube.com/@cil_associazione" isExternal>
          <Box transition="transform 0.2s" _hover={{ transform: 'scale(1.2)' }}>
            <FaYoutube size={24} />
          </Box>
        </Link>
        <Link href="https://www.tiktok.com/@cil_associazione" isExternal>
          <Box transition="transform 0.2s" _hover={{ transform: 'scale(1.2)' }}>
            <FaTiktok size={24} />
          </Box>
        </Link>
      </Stack>
    </Flex>
  );

  if (variant === 'site') {
    const fieldProps = {
      bg: C.bg,
      color: 'white',
      border: '1px solid',
      borderColor: 'rgba(255,255,255,0.16)',
      borderRadius: '8px',
      _placeholder: { color: 'gray.500' },
      _hover: { borderColor: 'rgba(255,255,255,0.3)' },
      _focus: { borderColor: C.yellow, boxShadow: `0 0 0 1px ${C.yellow}` },
    };
    const labelProps = {
      fontSize: 'sm',
      fontWeight: '700',
      color: 'gray.300',
      mb: 1.5,
    };
    const infoRows = [
      {
        icon: FaWhatsapp,
        label: 'Telefono / WhatsApp',
        value: '+39 353 468 2493',
        href: 'tel:+393534682493',
      },
      {
        icon: FaEnvelope,
        label: 'Email',
        value: 'info@cilassociazione.it',
        href: 'mailto:info@cilassociazione.it',
      },
      {
        icon: FaMapMarkerAlt,
        label: 'Sede operativa',
        value: 'Via Carlo Alberto 7, Chieri (TO)',
        href: 'https://www.google.com/maps/search/?api=1&query=Via+Carlo+Alberto+7+Chieri',
      },
    ];
    const socials = [
      [FaInstagram, 'Instagram', 'https://www.instagram.com/cil_associazione'],
      [
        FaFacebook,
        'Facebook',
        'https://www.facebook.com/profile.php?id=100091128373973',
      ],
      [FaYoutube, 'YouTube', 'https://www.youtube.com/@cil_associazione'],
      [FaTiktok, 'TikTok', 'https://www.tiktok.com/@cil_associazione'],
    ];

    return (
      <Box bg={C.bg} color="white" minH="100vh">
        <Container
          maxW="1120px"
          px={{ base: 4, md: 8 }}
          py={{ base: 12, md: 18 }}
        >
          <Eyebrow>Contatti</Eyebrow>
          <Heading
            as="h1"
            fontSize={{ base: '4xl', md: '5xl' }}
            fontWeight="800"
            letterSpacing="-0.02em"
            lineHeight="1.1"
            mb={4}
          >
            {title}
          </Heading>
          {description && (
            <Text
              fontSize="lg"
              color="gray.400"
              maxW="52ch"
              mb={{ base: 8, md: 12 }}
            >
              {description}
            </Text>
          )}

          <Grid
            templateColumns={{ base: '1fr', lg: '1.3fr 1fr' }}
            gap={{ base: 8, lg: 12 }}
            alignItems="start"
          >
            <Box
              as="form"
              onSubmit={handleSubmit}
              bg={C.surface}
              border="1px solid"
              borderColor={C.line}
              borderRadius="14px"
              p={{ base: 6, md: 8 }}
            >
              {error && (
                <Alert status="error" mb={5} borderRadius="8px">
                  <AlertIcon />
                  {error}
                </Alert>
              )}
              <FormControl id="email-site" mb={5} isRequired>
                <FormLabel {...labelProps}>Email</FormLabel>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="La tua email"
                  h="48px"
                  {...fieldProps}
                />
              </FormControl>

              <FormControl id="reason-site" mb={5} isRequired>
                <FormLabel {...labelProps}>Motivo</FormLabel>
                <Select
                  name="reason"
                  value={formData.reason}
                  onChange={handleChange}
                  isDisabled={Boolean(formData.proposalSlug)}
                  placeholder="Seleziona il motivo di contatto"
                  h="48px"
                  {...fieldProps}
                  sx={{ option: { background: '#1e2127', color: 'white' } }}
                >
                  {(reasonOptions.length ? reasonOptions : REASON_OPTIONS).map(
                    (value) => (
                      <option key={value} value={value}>
                        {formatReasonLabel(value)}
                      </option>
                    )
                  )}
                </Select>
              </FormControl>

              {formData.proposalSlug ? (
                <FormControl id="proposalSlug-site" mb={5}>
                  <FormLabel {...labelProps}>Proposta selezionata</FormLabel>
                  <Input
                    value={formData.proposalSlug}
                    isReadOnly
                    h="48px"
                    {...fieldProps}
                  />
                </FormControl>
              ) : null}

              <FormControl id="content-site" mb={6} isRequired>
                <FormLabel {...labelProps}>Messaggio</FormLabel>
                <Textarea
                  name="content"
                  value={formData.content}
                  onChange={handleChange}
                  placeholder="Scrivi il tuo messaggio qui..."
                  rows={6}
                  resize="vertical"
                  {...fieldProps}
                />
              </FormControl>

              <PrimaryButton
                type="submit"
                isLoading={isSubmitting}
                loadingText="Invio in corso..."
                w={{ base: 'full', sm: 'auto' }}
              >
                Invia messaggio
              </PrimaryButton>
            </Box>

            <VStack align="stretch" spacing={8}>
              <Box>
                <Eyebrow>Come raggiungerci</Eyebrow>
                <VStack
                  align="stretch"
                  spacing={0}
                  border="1px solid"
                  borderColor={C.line}
                  borderRadius="14px"
                  bg={C.surface}
                  overflow="hidden"
                >
                  {infoRows.map((row, i) => (
                    <Flex
                      key={row.label}
                      as="a"
                      href={row.href}
                      target={
                        row.href.startsWith('http') ? '_blank' : undefined
                      }
                      rel={
                        row.href.startsWith('http')
                          ? 'noopener noreferrer'
                          : undefined
                      }
                      gap={4}
                      align="center"
                      p={5}
                      borderBottom={
                        i < infoRows.length - 1 ? '1px solid' : 'none'
                      }
                      borderColor={C.line}
                      transition="background 0.2s"
                      _hover={{ bg: '#252930', '& .ct-v': { color: C.yellow } }}
                    >
                      <Icon as={row.icon} boxSize={5} color={C.yellow} />
                      <Box minW={0}>
                        <Text fontSize="xs" fontWeight="700" color="gray.500">
                          {row.label}
                        </Text>
                        <Text
                          className="ct-v"
                          fontWeight="700"
                          transition="color 0.2s"
                        >
                          {row.value}
                        </Text>
                      </Box>
                    </Flex>
                  ))}
                </VStack>
              </Box>

              <Box textAlign={{ base: 'center', lg: 'left' }}>
                <Eyebrow>Seguici</Eyebrow>
                <Flex
                  wrap="wrap"
                  columnGap={6}
                  rowGap={3}
                  justify={{ base: 'center', lg: 'flex-start' }}
                >
                  {socials.map(([SocialIcon, name, href]) => (
                    <Link
                      key={name}
                      href={href}
                      isExternal
                      aria-label={name}
                      title={name}
                      display="inline-flex"
                      color="white"
                      transition="all 0.2s"
                      _hover={{
                        color: C.yellow,
                        transform: 'translateY(-2px)',
                      }}
                    >
                      <Icon as={SocialIcon} boxSize={6} />
                    </Link>
                  ))}
                </Flex>
              </Box>
            </VStack>
          </Grid>
        </Container>
      </Box>
    );
  }

  if (splitLayout) {
    return (
      <Box
        maxW={splitLayout ? '100%' : maxW}
        mx={splitLayout ? 0 : 'auto'}
        mt={10}
        mb={16}
        px={{ base: 6, md: 10 }}
        py={10}
        borderRadius="2xl"
        bg={isLight ? 'gray.50' : 'gray.800'}
        boxShadow={isLight ? 'md' : '2xl'}
        color={isLight ? 'gray.800' : 'gray.100'}
        border="1px solid"
        borderColor={isLight ? 'gray.200' : 'gray.700'}
      >
        <form onSubmit={handleSubmit}>
          {description && (
            <Text
              textAlign="center"
              fontSize="lg"
              mb={6}
              color={isLight ? 'gray.600' : 'gray.300'}
            >
              {description}
            </Text>
          )}
          <Flex
            direction={{ base: 'column', md: 'row' }}
            gap={{ base: 4, md: 8 }}
            align={{ md: 'stretch' }}
            mb={8}
          >
            <Box flex={{ md: 1 }} minW={0}>
              {title && (
                <Heading
                  as="h1"
                  size={titleSize}
                  textAlign={{ base: 'center', md: 'left' }}
                  mb={2}
                  color={isLight ? 'gray.800' : 'white'}
                  textShadow={isLight ? 'none' : '0 1px 2px rgba(0,0,0,0.3)'}
                >
                  {title}
                </Heading>
              )}
              {error && (
                <Alert status="error" mb={4} borderRadius="md">
                  <AlertIcon />
                  {error}
                </Alert>
              )}
              <FormControl id="email-split" mb={4} isRequired>
                <FormLabel color={labelColor}>Email</FormLabel>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="La tua email"
                  focusBorderColor="blue.400"
                  _hover={{ borderColor: 'blue.400' }}
                  bg={isLight ? 'white' : 'gray.700'}
                  color={isLight ? 'gray.800' : 'white'}
                  borderColor={isLight ? 'gray.300' : 'gray.600'}
                  _placeholder={{ color: isLight ? 'gray.500' : 'gray.400' }}
                />
              </FormControl>
              <FormControl id="reason-split" mb={0} isRequired>
                <FormLabel color={labelColor}>Motivo</FormLabel>
                <Menu>
                  <MenuButton
                    as={Button}
                    w="full"
                    textAlign="left"
                    bg={menuButtonBg}
                    color={
                      formData.reason
                        ? isLight
                          ? 'gray.800'
                          : 'white'
                        : isLight
                          ? 'gray.500'
                          : 'gray.400'
                    }
                    borderColor={isLight ? 'gray.300' : 'gray.600'}
                    border="1px solid"
                    _hover={{ borderColor: 'red.400', bg: menuButtonBg }}
                    _focus={{ borderColor: 'red.400', bg: menuButtonBg }}
                    _expanded={{ borderColor: 'red.400', bg: menuButtonBg }}
                  >
                    {formData.reason
                      ? formatReasonLabel(formData.reason)
                      : 'Seleziona il motivo di contatto'}
                  </MenuButton>
                  <MenuList
                    bg={menuListBg}
                    borderColor={isLight ? 'gray.200' : 'gray.600'}
                  >
                    {(reasonOptions.length
                      ? reasonOptions
                      : REASON_OPTIONS
                    ).map((value) => (
                      <MenuItem
                        key={value}
                        value={value}
                        onClick={() =>
                          setFormData({ ...formData, reason: value })
                        }
                        bg={menuListBg}
                        color={isLight ? 'gray.800' : 'white'}
                        _hover={{ bg: isLight ? 'gray.50' : 'gray.600' }}
                      >
                        {formatReasonLabel(value)}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              </FormControl>
            </Box>
            <Box flex={{ md: 1 }} minW={0}>
              <FormControl id="content-split" mb={4} isRequired>
                <FormLabel color={labelColor}>Messaggio</FormLabel>
                <Textarea
                  name="content"
                  value={formData.content}
                  onChange={handleChange}
                  placeholder="Scrivi il tuo messaggio qui..."
                  rows={5}
                  resize="vertical"
                  focusBorderColor="pink.400"
                  _hover={{ borderColor: 'pink.400' }}
                  bg={isLight ? 'white' : 'gray.700'}
                  color={isLight ? 'gray.800' : 'white'}
                  borderColor={isLight ? 'gray.300' : 'gray.600'}
                  _placeholder={{ color: isLight ? 'gray.500' : 'gray.400' }}
                />
              </FormControl>
              <Button
                type="submit"
                isLoading={isSubmitting}
                loadingText="Invio in corso..."
                colorScheme="blue"
                bg="blue.600"
                color="white"
                width="full"
                _hover={{ bg: 'blue.700', color: 'white' }}
                transition="all 0.3s"
              >
                Invia Messaggio 🚀
              </Button>
            </Box>
          </Flex>
        </form>
        <Box
          pt={2}
          borderTopWidth="1px"
          borderColor={isLight ? 'gray.200' : 'gray.600'}
        >
          {contactsAndSocialBlock}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      maxW={splitLayout ? '100%' : maxW}
      mx={splitLayout ? 0 : 'auto'}
      mt={10}
      mb={16}
      px={{ base: 6, md: 10 }}
      py={10}
      borderRadius="2xl"
      bg={isLight ? 'gray.50' : 'gray.800'}
      boxShadow={isLight ? 'md' : '2xl'}
      color={isLight ? 'gray.800' : 'gray.100'}
      border="1px solid"
      borderColor={isLight ? 'gray.200' : 'gray.700'}
    >
      {formBlock}
      <Flex
        direction={{ base: 'column', md: 'row' }}
        align="center"
        justify="space-between"
        mt={10}
        gap={6}
        color={isLight ? 'gray.700' : 'gray.200'}
        flexWrap="wrap"
      >
        <Stack spacing={3} align="start">
          <Flex align="center" gap={2}>
            <FaWhatsapp size={20} />
            <Text fontSize="md">+39 353 468 2493</Text>
          </Flex>
          <Flex align="center" gap={2}>
            <FaPhoneAlt size={18} />
            <Text fontSize="md">+39 353 468 2493</Text>
          </Flex>
          <Flex align="center" gap={2}>
            <FaEnvelope size={18} />
            <Text fontSize="md">info@cilassociazione.it</Text>
          </Flex>
          <Flex align="center" gap={2}>
            <FaMapMarkerAlt size={20} />
            <Text fontSize="md">Via Carlo Alberto 7, Chieri (TO)</Text>
          </Flex>
        </Stack>
        <Stack direction={{ base: 'row', md: 'column' }} gap={4} align="center">
          <Link href="https://www.instagram.com/cil_associazione" isExternal>
            <Box
              transition="transform 0.2s"
              _hover={{ transform: 'scale(1.2)' }}
            >
              <FaInstagram size={24} />
            </Box>
          </Link>
          <Link
            href="https://www.facebook.com/profile.php?id=100091128373973"
            isExternal
          >
            <Box
              transition="transform 0.2s"
              _hover={{ transform: 'scale(1.2)' }}
            >
              <FaFacebook size={24} />
            </Box>
          </Link>
          <Link href="https://www.youtube.com/@cil_associazione" isExternal>
            <Box
              transition="transform 0.2s"
              _hover={{ transform: 'scale(1.2)' }}
            >
              <FaYoutube size={24} />
            </Box>
          </Link>
          <Link href="https://www.tiktok.com/@cil_associazione" isExternal>
            <Box
              transition="transform 0.2s"
              _hover={{ transform: 'scale(1.2)' }}
            >
              <FaTiktok size={24} />
            </Box>
          </Link>
        </Stack>
      </Flex>
    </Box>
  );
};

export default ContactForm;
